import { useCallback, useEffect, useState } from "react"

import {
  createListOption,
  deleteListOption,
  getListOptions,
  updateListOption
} from "./list-options.services.ts"
import type {
  ManagedListKey,
  ManagedListOption,
  ManagedListOptionPatch
} from "./list-options.types.ts"

type Failure = { message: string }

const readError = (data: unknown): string => {
  const details = (data as { details?: { message?: string } })?.details
  if (details?.message) return details.message

  // DRF field errors arrive as { field: ["..."] }; the screen has one control
  // per row, so the first message is the one that names the refusal.
  const first = Object.values((data as Record<string, unknown>) ?? {}).find(
    (value) => Array.isArray(value) && typeof value[0] === "string"
  )
  return (first as string[] | undefined)?.[0] ?? ""
}

export function useManagedListOptions(listKey: ManagedListKey) {
  const [options, setOptions] = useState<ManagedListOption[]>([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [truncated, setTruncated] = useState(false)
  const [saving, setSaving] = useState(false)

  // A reload after a write is silent: raising `loading` would swap the table
  // for the skeleton, and remounting it drops the reader's scroll position
  // back to the top of the list.
  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true)
      const response = await getListOptions(listKey)
      if (response.ok) {
        const results = response.data?.results ?? []
        setOptions(results)
        // A count past the page means an order the reader cannot complete.
        setTruncated((response.data?.count ?? 0) > results.length)
        setLoadFailed(false)
      } else {
        setLoadFailed(true)
      }
      if (!silent) setLoading(false)
    },
    [listKey]
  )

  useEffect(() => {
    void load()
  }, [load])

  // Every mutation reloads rather than patching local state: the list sorts
  // itself A→Z server-side, so a create or a rename can move a row and a local
  // edit would disagree with where it lands. A refusal reloads too.
  const run = useCallback(
    async (
      call: () => Promise<{ ok: boolean; data?: unknown }>
    ): Promise<Failure | null> => {
      setSaving(true)
      const response = await call()
      await load({ silent: true })
      setSaving(false)
      return response.ok ? null : { message: readError(response.data) }
    },
    [load]
  )

  const add = useCallback(
    (label: string) => run(() => createListOption(listKey, label)),
    [run, listKey]
  )

  const patch = useCallback(
    (id: string, data: ManagedListOptionPatch) =>
      run(() => updateListOption(id, data)),
    [run]
  )

  const remove = useCallback(
    (id: string) => run(() => deleteListOption(id)),
    [run]
  )

  return {
    options,
    loading,
    loadFailed,
    truncated,
    saving,
    reload: load,
    add,
    patch,
    remove
  }
}
